import React, { useEffect } from 'react';
import { Paper, Typography, Stack } from '@mui/material';
import { useForm } from 'react-hook-form';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { IconLogin } from '@tabler/icons-react';
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from 'zod';

import CustomButton from '@components/button/customButton';
import ControlledTextField from '@components/inputs/controlledTextField';

import { AdminApiEndpoints } from '@configuration/apiEndpoints';
import { useSnackbar } from '@context/snackbarContext';
import { useAuthentication } from '@redux/authentication/hook';

import LoginBackground from '@assets/image/login-background.png';
import Logo from "@assets/logo/zhushan-vertical-logo.png"

const schema = z.object({
  username: z.string().min(1, { message: 'Username is required' }),
  password: z.string().min(1, { message: 'Password is required' }),
});

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const openSnackbar = useSnackbar();
  const { updateUser, updateIsAuthenticated, isAuthenticated, updateModuleAccess } = useAuthentication();
  const { from } = location.state || { from: { pathname: "/admin" } };

  var accessToken = localStorage.getItem("accessToken");

  const { control, handleSubmit, formState: { errors, isSubmitting } } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      username: "",
      password: "",
    },
  })

  const setModuleAccess = (user) => {
    const moduleAccess = [
      ...new Set(
        user?.userRoles?.flatMap((userRole) =>
          userRole?.role?.roleModules
            ?.map((rm) => {
              return {
                moduleGuid: rm?.moduleGuid,
                grandAccess: rm?.grandAccess,
                isAdmin: rm?.isAdmin,
              };
            })
        )
      ),
    ];
    updateModuleAccess(moduleAccess);
  }

  const autoSignIn = async () => {
    await axios.post(AdminApiEndpoints.validateToken, accessToken).then((res) => {
      if (res.data?.accessToken) {
        localStorage.setItem("accessToken", res.data?.accessToken);
      }
      updateUser(res.data?.user);
      setModuleAccess(res?.data?.user)
      updateIsAuthenticated(true);
      navigate(from.pathname, { replace: true });
    });
  };

  useEffect(() => {
    if (accessToken) {
      // sign in with available token
      autoSignIn();
    }
  }, []);

  const onSubmit = async (data) => {
    await axios
      .post(AdminApiEndpoints.login, {username: data?.username, password: data?.password}, { requiresAuth: false })
      .then((res) => {
        // console.log(res.data)
        localStorage.setItem("accessToken", res?.data?.accessToken);
        updateUser(res?.data?.user);
        setModuleAccess(res?.data?.user)
        updateIsAuthenticated(true);
        navigate(from.pathname, { replace: true });
        openSnackbar("Successfully signed in.", "success");
      })
      .catch((error) => {
        if (error?.response) openSnackbar(error?.response?.data, "error");
        else openSnackbar(error?.message, "error");
      });
  };

  return isAuthenticated ? (
    <Navigate to={from.pathname} replace={true} />
  ) : (
    <form
      onSubmit={handleSubmit(onSubmit)}
      style={{
        position: 'relative',
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        backgroundColor: '#FAFAFA',
        backgroundImage: `url(${LoginBackground})`,
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
        width: "100%"
      }}
    >
      <Paper
        sx={{
          p: "30px 35px",
          m: "10px",
          boxShadow: "none",
          borderRadius: "10px",
          border: "1px solid #E5E5E5",
          overflowX: "auto",
          maxWidth: "25rem",
          width: "100%",
          zIndex: 1
        }}
      >
        <div style={{display: 'flex', justifyContent: 'center',}}>
          <img src={Logo} width={"80px"} alt='Zhu Shan'/>
        </div>
        <Stack spacing={3} marginY={'35px'}>
          <ControlledTextField 
            name="username"
            control={control}
            label="Username"
            // disabled={fetchCategoryMutation.isPending || fetchCategoryMutation.isError}
          />
          <ControlledTextField 
            name="password"
            control={control}
            label="Password"
            type='password'
            // disabled={fetchCategoryMutation.isPending || fetchCategoryMutation.isError}
          />       
        </Stack>
        <CustomButton type="submit" fullWidth loading={isSubmitting}>Log In</CustomButton>
      </Paper>
      <div style={{position: 'absolute', bottom: '30px', zIndex: 0}}>
        <Typography textAlign={'center'} fontSize={'12px'} color='#FFF'>Powered by Codex Lab Sdn. Bhd.</Typography>
      </div>
    </form>
  )
}
